/**
 * RWBR — THE BOOTH: the director.
 *
 * NARRATION_MODEL §8.1: *"The largest source of repetition is narrating things
 * that did not deserve narration… The director's first job is not choosing
 * words — it is choosing silence."*
 *
 * That is this module, and it is the most important one in the booth. A
 * fifty-turn race emits about 129 events. The booth speaks 10–20 times. Nothing
 * else in the design matters as much: if the booth talks on every event it MUST
 * repeat itself, because most events are ordinary and there are only so many
 * ways to say that nothing much happened.
 *
 * Everything here is deterministic and cheap — it runs before any model is
 * called, which is also the cost control (D-027).
 *
 * WHAT IT DECIDES
 *   1. Which turns are worth a beat at all (a score, then a cut).
 *   2. Which quiet stretches compress into one line (§8.6).
 *   3. Which ANGLE each beat takes (§8.2) — the real variety mechanism, because
 *      seven angles chosen from game state beat seven phrasings of one angle,
 *      and the available angles rotate naturally as the race state changes.
 */

import type { RaceResult } from '../race.ts';
import type { TurnResult } from '../turn.ts';

export const CITE_SILENCE = 'NARRATION_MODEL §8.1 — the director\'s first job is choosing silence';
export const CITE_ANGLES = 'NARRATION_MODEL §8.2 — vary the angle, not the phrasing';
export const CITE_COMPRESS = 'NARRATION_MODEL §8.6 — compress quiet stretches';

/**
 * HOW OFTEN THE BOOTH SPEAKS — D-EZ-74.
 *
 * §8.7 shipped with 10–20 beats on a fifty-turn race. Howard, 21 Aug 2026,
 * asked for *"MORE. More conversation, more descriptive, more colorful, more
 * informative"* and chose roughly double when asked how far.
 *
 * ⚠ THE COUNT IS THE EASY HALF. The band is two numbers; the reason it was
 * SET at ten and twenty is that the booth had about that much to say. Doubling
 * it against the old researcher would have produced twice the beats out of the
 * same eleven fact kinds, the five-gram gate would have refused the surplus,
 * and every refusal falls back to the engine's plainer sentence — a louder
 * booth saying less. That is why this line moved LAST in D-EZ-74, after the
 * researcher learned pit road, the feuds, the laps down, the shops and the
 * numbers on the doors, and after the phrasing pools were widened to carry it.
 *
 * SILENCE IS STILL A CHOICE (§8.1). Twice as often is not "every turn": a
 * fifty-turn race still has fifteen or more turns nobody remarks on, and the
 * quiet-stretch compression in §8.6 still speaks for the rest. A booth with
 * nothing to say and nowhere to be quiet has no way left to signal that
 * something mattered.
 */
export const BEATS_PER_TURN_MIN = 20 / 50;
export const BEATS_PER_TURN_MAX = 34 / 50;

/**
 * The seven angles of §8.2. `silence` is one of them and is listed here on
 * purpose — not saying anything is a choice the director makes, not an absence
 * of one.
 */
export type Angle =
  | 'move'
  | 'defence'
  | 'stretch'
  | 'consequence'
  | 'season'
  | 'rivalry'
  | 'silence';

export const ANGLES: readonly Angle[] = ['move', 'defence', 'stretch', 'consequence', 'season', 'rivalry'];

/**
 * §8.2: "the director records the last angle used per driver and per race, and
 * will not reuse an angle within a short window."
 */
export const ANGLE_WINDOW = 3;

/** §8.7 gate: no single angle may exceed 30% of beats. */
export const ANGLE_SHARE_CAP = 0.3;

// ---------------------------------------------------------------------------
// Scoring
// ---------------------------------------------------------------------------

/**
 * How much this turn deserves to be spoken about.
 *
 * The weights are ordinal, not physical — what matters is that an eight-car
 * wreck outranks a routine gap-fill, which is exactly what §4 asks the director
 * to do. Anything scoring below `SPEAK_FLOOR` is a candidate for silence.
 */
export interface TurnScore {
  turn: number;
  score: number;
  /** Why it scored — carried through so a reader can audit the choice. */
  reasons: string[];
}

export const SPEAK_FLOOR = 20;

/**
 * D-003: *"A fast, cheap model writes routine turns; a stronger one handles
 * wrecks, lead changes, the Big One, and the finish."*
 *
 * The threshold is the director's own arithmetic rather than a second opinion:
 * `scoreTurn` gives a multi-car wreck 60+, a lead change 40, a caution 20 and
 * ordinary racing single digits. 40 is therefore "the lead changed hands, or
 * something bigger" — the exact list D-003 names.
 *
 * It LIVES HERE, beside the scale it is measured against. `booth.ts` needs it
 * for §6's expanding beat and must not reach up into `src/lib` for a number the
 * director owns; `narrate.ts`, which defined it first, re-exports it so nothing
 * that imported it from there had to change.
 */
export const BIG_MOMENT_SCORE = 40;

export function scoreTurn(t: TurnResult, race: RaceResult): TurnScore {
  const reasons: string[] = [];
  let score = 0;

  const add = (n: number, why: string): void => {
    score += n;
    reasons.push(why);
  };

  // A multi-car incident is the loudest thing that happens in this game.
  if (t.collected.length >= 8) add(60 + t.collected.length, `${t.collected.length} cars collected`);
  else if (t.collected.length >= 3) add(35 + t.collected.length, `${t.collected.length} cars collected`);
  else if (t.collected.length > 0) add(18, `${t.collected.length} car in trouble`);

  // The lead changing hands is the thing the broadcast exists to show.
  if (t.leadChangedTo !== null) add(40, 'lead change');

  if (t.retiredThisTurn.length > 0) add(22 + t.retiredThisTurn.length * 4, `${t.retiredThisTurn.length} out`);
  if (t.yellowCameOut) add(20, 'caution');
  if (t.pit) add(14, 'pit turn');

  // The last few turns of a race carry weight on their own — a pass for
  // seventh on turn 3 is not a pass for seventh on the final turn.
  const toGo = race.turnsRun - t.turn;
  if (toGo <= 2) add(24, `${toGo} to go`);
  else if (toGo <= 5) add(12, `${toGo} to go`);
  if (t.turn === 1) add(16, 'the start');

  // Ordinary racing. Enough to be eligible, never enough on its own.
  const passes = t.events.filter((e: { momentTag: string }) => e.momentTag.startsWith('challenge.') || e.momentTag.startsWith('duel.')).length;
  if (passes > 0) add(Math.min(14, 4 + passes * 2), `${passes} on-track moves`);

  return { turn: t.turn, score, reasons };
}

// ---------------------------------------------------------------------------
// Choosing what gets said
// ---------------------------------------------------------------------------

export interface Beat {
  /** The turn this beat speaks about; the FIRST turn where it compresses. */
  turn: number;
  kind: 'moment' | 'quiet' | 'open' | 'close';
  angle: Angle;
  score: number;
  /** For a compressed quiet stretch: how many turns it covers. */
  turnsCovered: number;
  reasons: string[];
}

/** The band §8.7 gates on, in beats, for a race of this length. */
export function beatBand(turnsRun: number): { min: number; max: number } {
  const min = Math.max(2, Math.round(turnsRun * BEATS_PER_TURN_MIN));
  const max = Math.max(min + 1, Math.round(turnsRun * BEATS_PER_TURN_MAX));
  return { min, max };
}

/**
 * Pick the turns that COULD speak.
 *
 * Deliberately more than will be used. The editor rejects lines — that is its
 * job — and an earlier version passed exactly `max` candidates through, so a
 * race where several were refused finished under the §8.7 floor. A 28-turn
 * Martinsville came out at 8.9 beats per fifty turns against a floor of 10.
 *
 * So the pool is oversized and the CUT MOVES to the point of acceptance, in
 * `callRace`. Choosing is cheap; being short of material is not recoverable.
 */
export function chooseTurns(race: RaceResult): TurnScore[] {
  const scored = race.turns.map((t) => scoreTurn(t, race)).sort((a, b) => b.score - a.score || a.turn - b.turn);
  const { min, max } = beatBand(race.turnsRun);

  const loud = scored.filter((s) => s.score >= SPEAK_FLOOR);
  const want = Math.min(scored.length, max + Math.max(6, min));

  /*
    ⚠ THE FLOOR IS A FLOOR, NOT A PREFERENCE — D-EZ-74.

    This used to read `loud.length >= want ? loud.slice(0, want) : scored.slice(0, want)`
    — if there were not enough turns worth speaking about, it made up the
    quota out of the dull ones. That was harmless while the quota was ten
    beats in fifty turns, because ten turns of a stock car race are almost
    always genuinely eventful.

    At thirty-four it stopped being harmless. `repetition.test.ts` caught the
    consequence rather than the cause: across ten full races, the number of
    COMPRESSED QUIET STRETCHES went to zero. §8.6's "three uneventful turns
    produce one line" needs uneventful turns to exist, and the director was
    scooping every last one of them into the candidate pool to hit a number.
    Ten races, and not one moment of the booth stepping back.

    So the floor holds. A race with plenty happening gets a loud booth; a
    processional one gets a quiet booth and its quiet stretches back. MORE
    COMMENTARY HAS TO COME FROM HAVING MORE TO SAY. A booth that hits its beat
    count by remarking on nothing is the padding this whole design exists to
    refuse, and it is exactly what Howard asked for the opposite of.
  */
  const chosen = loud.slice(0, want);

  return chosen.sort((a, b) => a.turn - b.turn);
}

/**
 * Runs of consecutive UNSPOKEN turns, which §8.6 compresses into a single line.
 *
 * Only runs of two or more are returned. A single quiet turn between two loud
 * ones needs no comment at all — saying "they ran four laps clean" about one
 * turn is precisely the padding this whole design is trying to avoid.
 */
export function quietRuns(race: RaceResult, spoken: ReadonlySet<number>): { from: number; to: number }[] {
  const runs: { from: number; to: number }[] = [];
  let start: number | null = null;

  for (let turn = 1; turn <= race.turnsRun; turn++) {
    if (spoken.has(turn)) {
      if (start !== null && turn - start >= 2) runs.push({ from: start, to: turn - 1 });
      start = null;
    } else if (start === null) {
      start = turn;
    }
  }
  if (start !== null && race.turnsRun + 1 - start >= 2) runs.push({ from: start, to: race.turnsRun });
  return runs;
}

/**
 * Which angles this turn could honestly support.
 *
 * The critical word is HONESTLY. `rivalry` is only offered when a feud actually
 * exists between the drivers involved; `season` only when the caller has a
 * verified season fact to hand. An angle that cannot be supported by state is
 * never offered, which is what stops the booth reaching for a callback it would
 * have to invent.
 */
export function availableAngles(
  t: TurnResult,
  opts: {
    hasRivalry: boolean;
    hasSeasonFact: boolean;
    changedPosition: boolean;
    hasPosition?: boolean;
    hasOpponent?: boolean;
  },
): Angle[] {
  const out: Angle[] = [];

  const contested = t.events.some(
    (e: { momentTag: string }) => e.momentTag.startsWith('challenge.') || e.momentTag.startsWith('duel.'),
  );
  if (t.collected.length > 0 || contested) out.push('move');
  // The defence angle needs somebody to have been defending.
  if ((t.collected.length > 0 || contested) && opts.hasOpponent !== false) out.push('defence');

  out.push('stretch');

  // Consequence works off a KNOWN position, not only a changed one — "that's
  // fourth" is a true and useful thing to say about a car that held station.
  if (opts.changedPosition || t.leadChangedTo !== null || opts.hasPosition) out.push('consequence');
  if (opts.hasSeasonFact) out.push('season');
  if (opts.hasRivalry) out.push('rivalry');

  // A turn with too few angles cannot satisfy the 30% share cap, so the pool is
  // never allowed to fall below three: with eight moments and three angles the
  // cap needs two of each, and two angles simply cannot spread eight beats.
  if (!out.includes('consequence')) out.push('consequence');
  return [...new Set(out)];
}

/**
 * Choose an angle, respecting both the recency window (§8.2) and the 30% share
 * cap (§8.7).
 *
 * Deterministic: given the same history and the same options it always returns
 * the same angle. Variety comes from the RACE changing, not from a shuffle —
 * which is what makes it reproducible and testable.
 */
export function chooseAngle(
  available: readonly Angle[],
  recent: readonly Angle[],
  used: ReadonlyMap<Angle, number>,
  beatsSoFar: number,
  expectedTotal = 0,
  /**
   * §6 — "Big turns expand." A turn the director scored as a big moment may not
   * be silenced by an angle quota. See the note at the bottom of this function.
   */
  big = false,
): Angle {
  if (available.length === 0) return 'silence';

  const window = recent.slice(-ANGLE_WINDOW);

  // The cap is FORWARD-looking: would using this angle now put it over 30% of
  // the race's expected beats?
  //
  // Measuring the share so far needed a warm-up — you cannot be over 30% on
  // beat one — and that warm-up was the hole. With a six-beat grace period and
  // only eight moments in the race, 'defence' took three of them before the cap
  // ever engaged, and finished at 38%. Against the projected total there is no
  // warm-up to exploit.
  const denom = expectedTotal > 0 ? expectedTotal : Math.max(1, beatsSoFar) / ANGLE_SHARE_CAP;
  const capped = (a: Angle): boolean => ((used.get(a) ?? 0) + 1) / denom > ANGLE_SHARE_CAP;

  // Least-recently-used first, then least-used overall. Both are properties of
  // the history rather than of chance.
  const rank = (a: Angle): number => {
    const sinceUsed = window.lastIndexOf(a);
    const recencyPenalty = sinceUsed === -1 ? 0 : (ANGLE_WINDOW - sinceUsed) * 100;
    return recencyPenalty + (used.get(a) ?? 0);
  };

  const fresh = available.filter((a) => !window.includes(a) && !capped(a));
  const pool = fresh.length > 0 ? fresh : available.filter((a) => !capped(a));

  /**
   * Nothing left that this race can honestly carry without breaking the share
   * cap. §8.1 already gave the answer to that: say nothing. The old behaviour
   * here was to ignore the cap and speak anyway, which is how 'defence' finished
   * a race on 38% — the safety valve quietly repealed the rule it was guarding.
   *
   * ⚠ EXCEPT FOR A BIG MOMENT, and this was found the hard way.
   *
   * Howard, 16 Aug 2026, on a Big One: *"this is the story and currently, from
   * the booth, there's very little."* Measured on seed 31337: turn 20 scored
   * **183** — twenty-one cars collected, fifteen out of the race, three men
   * hurt, by far the biggest turn of the afternoon and more than double the
   * next — and the booth said NOTHING about it, while spending beats on turns
   * that scored 6.
   *
   * The quota did it. Every angle the turn could honestly take was already at
   * its 30% share, so this returned `silence` and the largest wreck of the race
   * went uncalled.
   *
   * That is the anti-repetition device eating the thing it exists to serve.
   * §8.1's silence is explicitly about *"narrating things that did not deserve
   * narration"*; a twenty-one-car wreck is not that, and §6 says so in as many
   * words — *"Big turns expand."* So a big moment takes the least-used angle it
   * can rather than none.
   *
   * THE HARD GATE IS NOT REPEALED. §8.7's "any single angle ≤ 30%" is enforced
   * downstream by `withinAngleCap`, which rebuilds the whole call against a
   * tighter projection until it passes. This only removes silence as the answer
   * for the one case where silence is worse than any angle.
   */
  if (pool.length === 0) {
    if (!big) return 'silence';
    return [...available].sort(
      (a, b) => (used.get(a) ?? 0) - (used.get(b) ?? 0) || ANGLES.indexOf(a) - ANGLES.indexOf(b),
    )[0];
  }

  return [...pool].sort((a, b) => rank(a) - rank(b) || ANGLES.indexOf(a) - ANGLES.indexOf(b))[0];
}
